import React, { PropsWithChildren, useState, useEffect, useRef } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { useEnteredView } from '@fiverr-private/hooks';
import { localStorage } from '@fiverr-private/futile';
import { SafeComponent } from '@fiverr-private/orca';
import {
  reportConsultationExists,
  reportBookConsultationClick,
  bookConsultationButtonView,
  consultationGetStartedClick,
  closePopUpClick,
} from '../../utils/events';
import { CONSULTATION_ONBOARDING_MODAL_SHOWN_IN_LS, QUERY_PARAMS, OVERLAY_TYPES } from '../../constants';
import {
  enrichSignInModalOptions,
  genericErrorHandler,
  getConsultationOverlaySessionStorage,
  removeConsultationOverlaySessionStorage,
  triggerSignInApi,
  triggerSignUpApi,
} from '../../utils';
import ConsultationOnboardingModal from '../ConsultationOnboardingModal/lazy';
import BookConsultationDrawer from '../BookConsultationDrawer/lazy';
import { ConsultationProps } from './types';

const Consultation = ({
  consultationData,
  sellerId,
  sellerName,
  profilePhoto,
  biEnrichment,
  mixpanelEnrichment,
  learnMoreLink,
  currency,
  children,
  className,
  sourcePage,
  triggerData,
}: PropsWithChildren<ConsultationProps>) => {
  const [showConsultationOnboardingModal, setShowConsultationOnboardingModal] = useState(false);
  const [showConsultationDrawer, setShowConsultationDrawer] = useState(false);
  const observableRef = useRef(null);
  const {
    queryParameters: {
      [QUERY_PARAMS.AUTO_OPEN_CONSULTATION_DRAWER]: autoOpenDrawer,
      [QUERY_PARAMS.AUTO_OPEN_CONSULTATION_MODAL]: autoOpenModal,
    },
    userId,
    experience,
  } = getContext();
  useEnteredView(observableRef, {
    threshold: 1,
    onEntered: () => {
      bookConsultationButtonView(biEnrichment, sellerId);
    },
  });

  useEffect(() => {
    reportConsultationExists(mixpanelEnrichment);

    if (!userId) {
      return;
    }

    const isShown = !!localStorage.get(CONSULTATION_ONBOARDING_MODAL_SHOWN_IN_LS);
    const storedOverlayType = getConsultationOverlaySessionStorage();

    if (autoOpenDrawer || storedOverlayType === OVERLAY_TYPES.DRAWER) {
      setShowConsultationOnboardingModal(false);
      setShowConsultationDrawer(true);

      removeConsultationOverlaySessionStorage();
    } else if (autoOpenModal || storedOverlayType === OVERLAY_TYPES.MODAL) {
      if (isShown) {
        setShowConsultationDrawer(true);
      } else {
        setShowConsultationOnboardingModal(true);
      }

      removeConsultationOverlaySessionStorage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConsultationClick = async (inOnboardingModal = false) => {
    const isModalShown = !!localStorage.get(CONSULTATION_ONBOARDING_MODAL_SHOWN_IN_LS);

    if (!userId) {
      const enrichedModalOptions = enrichSignInModalOptions({
        isModalShown,
        sourcePage,
        triggerData,
      });
      if (experience.isBusiness) {
        await triggerSignUpApi(enrichedModalOptions);
      } else {
        await triggerSignInApi(enrichedModalOptions);
      }
      return;
    }

    if (!inOnboardingModal) {
      reportBookConsultationClick(biEnrichment, mixpanelEnrichment);
    }

    if (inOnboardingModal || isModalShown) {
      setShowConsultationOnboardingModal(false);
      setShowConsultationDrawer(true);
      consultationGetStartedClick(biEnrichment, sellerId);
    } else {
      setShowConsultationOnboardingModal(true);
    }
  };

  const closeConsultationOnboardingModal = () => {
    closePopUpClick(biEnrichment, { sellerId, popupSource: 'book_a_consultation_pop_up' });
    setShowConsultationOnboardingModal(false);
  };

  const closeConsultationDrawer = () => {
    closePopUpClick(biEnrichment, { sellerId, popupSource: 'book_a_consultation_drawer' });
    setShowConsultationDrawer(false);
  };

  return (
    <>
      <div className={className} onClick={() => onConsultationClick()} ref={observableRef}>
        {children}
      </div>
      {showConsultationOnboardingModal && (
        <ConsultationOnboardingModal
          sellerId={sellerId}
          username={sellerName}
          onCtaClick={() => onConsultationClick(true)}
          onClose={closeConsultationOnboardingModal}
          mixpanelEnrichment={mixpanelEnrichment}
          learnMoreLink={learnMoreLink}
          biEnrichment={biEnrichment}
        />
      )}
      {showConsultationDrawer && (
        <BookConsultationDrawer
          username={sellerName}
          sellerId={sellerId}
          avatar={profilePhoto}
          consultationData={consultationData}
          onClose={closeConsultationDrawer}
          mixpanelEnrichment={mixpanelEnrichment}
          biEnrichment={biEnrichment}
          currency={currency}
          sourcePage={sourcePage}
        />
      )}
    </>
  );
};

export default SafeComponent(Consultation, genericErrorHandler);
